// External
import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';

// Internal
import { ValuesService } from '../../values/values.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfigService, LogoutType } from '../../config/config.service';
import { Owner, ProfilesService } from '../../services/process/profiles/profiles.service';
import { MessageService } from '../../services/core/message.service';
import { TranslateService } from '@ngx-translate/core';
import { UsefulService } from '../../services/global/useful/useful.service';
import { RequestsService } from '../../services/global/request-service/requests.service';
import { PushService } from '../../services/core/push.service';
import { ThemeService } from '../../services/core/theme.service';
import { ProductsConfigService } from '../../config/products.config.service';
import { AdobeDataLayerService } from '../../services/core/adobe.datalayer.service';
import { ModalName, ModalRoutelessService } from '../ui/ui-modal-routeless/modal.routeless.service';
import { ContextInterface, IContextService } from '../../services/interfaces/icontext/icontext.service';
import { ModalSize } from '../../services/global/modal/Modal.model';

interface Menu {
    owner: Owner,
    list: Array<any>
}

@Component({
    selector: 'app-user-details',
    templateUrl: './user-details.component.html',
    styleUrls: ['./user-details.component.scss']
})
export class UserDetailsComponent implements OnInit {

    @Output() statusDrawer: EventEmitter<string> = new EventEmitter();
    public contextsInterface = [] as ContextInterface[];
    public menu: Menu = {
        owner: {} as Owner,
        list: []
    };

    themeSelected;

    private readonly onDestroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly messageService: MessageService,
        private readonly themeService: ThemeService,
        private readonly router: Router,
        private readonly translate: TranslateService,
        private readonly requestsService: RequestsService,
        private readonly profilesService: ProfilesService,
        private readonly usefulService: UsefulService,
        private readonly valuesService: ValuesService,
        private readonly modalRoutelessService: ModalRoutelessService,
        private readonly adobeDataLayerService: AdobeDataLayerService,
        private readonly pushService: PushService,
        private readonly productsConfigService: ProductsConfigService,
        private readonly icontextService: IContextService,
        public readonly configService: ConfigService
    ) { }

    //*
    //*  Life-Cycle Hooks
    //*
    //#region
    ngOnInit() {
        this.messageService.getMessage()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(message => {
            switch (message.text) {
                case this.valuesService.events.themeChange:
                    this.themeSelected = this.themeService.getTheme().name === 'default' ? false : true;
                    break;
                case this.valuesService.events.languageChange:
                case this.valuesService.events.ownerChange:
                    this.getUserDetails();
                    this.initVars();
                    break;
                default:
                    break;
            }
        });

        this.messageService.getDynamicSubject(this.valuesService.events.ownerChange)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
                this.getUserDetails();
                this.initVars();
            });

        // translateService event for init and change of lang
        this.translate.onLangChange.subscribe(_ => this.initVars());
        this.contextsInterface = this.icontextService.getContextsInterface();

    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
    //#endregion

    //*
    //* Helper functions
    //*
    //#region

    /**
    * Function that inits variables. This helps when reinitialising is needed
    * @param {none}
    * @returns {nothing} Inits variables
    */
    private initVars() {
        this.menu.list = [
            {
                label: this.configService.accountIsExternal()
                    ? `${this.productsConfigService.getManufacturerName()} ${this.translate.instant('mail.header.accout')}`
                    : this.translate.instant('mail.header.accout'),
                display: true,
                icon: this.valuesService.connectIcons.account,
                url: this.configService.accountIsExternal()
                    ? this.adobeDataLayerService.addRefAndVisitor(this.configService.config.externalAccountLink)
                    : this.valuesService.centralPaths.account.path,
                action: event => {
                    event.preventDefault();
                    this.configService.accountIsExternal() ? this.redirectToAccountWithToken()
                                                           : this.navigateTo(this.valuesService.centralPaths.account.path);
                }
            },
            {
                label: this.translate.instant('account.session.recent.title.menu'),
                display: this.configService.getAccountSessions(),
                icon: this.valuesService.connectIcons.session,
                url: this.valuesService.centralPaths.account.path + this.valuesService.centralPaths.account.sessions.path,
                action: event => {
                    event.preventDefault();
                    this.navigateTo(this.valuesService.centralPaths.account.path + this.valuesService.centralPaths.account.sessions.path);
                }
            },
            {
                label: this.translate.instant('devices.settings.settings'),
                display: this.configService.getAccountSettings(),
                icon: this.valuesService.connectIcons.settings,
                url: this.valuesService.centralPaths.account.path + this.valuesService.centralPaths.account.settings.path,
                action: event => {
                    event.preventDefault();
                    this.navigateTo(this.valuesService.centralPaths.account.path + this.valuesService.centralPaths.account.settings.path);
                }
            },
            {
                label: this.translate.instant('redeem.meniu.logout'),
                display: true,
                icon: this.valuesService.connectIcons.logout,
                url: '',
                action: event => {
                    event.preventDefault();
                    this.pushService.unregisterPush();
                    this.requestsService.logout(LogoutType.REDIRECT_TO_LOGOUT_PAGE).pipe(takeUntil(this.onDestroy$)).subscribe();
                }
            }
        ];
        this.themeSelected = this.themeService.getTheme().name === 'default' ? false : true;

    }

    /**
    * Gets account owner info and sets them on local object
    * @private
    * @memberof UserDetailsComponent
    */
    private getUserDetails(): void {
        const owner = this.profilesService.getOwner();

        if (owner) {
            const firstName = this.profilesService.getOwnerFirstName();
            const email = this.usefulService.sanitizeValues(owner?.email);

            this.menu.owner = {
                firstname: firstName ? firstName : email,
                email: email,
                groups: this.profilesService.getGroups(),
                current_context_id: this.profilesService.getCurrentContextId()
            };
        }
    }

    /**
    * This redirects user to Bitdefender Account with auth token ( auto-login )
    * @param {none}
    * @returns {nothing} Navigates user to Bitdefender Account loggedin or not
    */
    private redirectToAccountWithToken() {
        const url = this.adobeDataLayerService.addRefAndVisitor(this.configService.config.externalAccountLink);
        window.open(url, '_blank');
    };

    /**
    * This navigates internally SPA style and close all drawers
    * @param {string} path Destination
    * @returns {nothing} Navigates user
    */
    private navigateTo(path) {
        this.statusDrawer.emit('close');
        this.router.navigate([path]);
    }

    public toggleTheme() {
        if(this.themeService.getTheme().name === 'default') {
            this.themeService.setDarkTheme();
        } else {
            this.themeService.setLightTheme();
        }
    }

    /**
    * Opens switch context modal
    * @public
    * @param {string} contextId
    * @returns {void} opens modal
    */
    public switchContext(contextId): void {
        this.modalRoutelessService.open(ModalName.switchContextModal, {size: ModalSize.SM}, {contextId});
    }
    //#endregion

}
